import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { DEFAULT_SCORE_METHOD, METHOD_ORDER, type ScoreMethod } from '../lib/metrics-series.js';
import { DEFAULT_PLAYBACK_RATE, isPlaybackRate, type PlaybackRate } from '../lib/playback-rate.js';

/** The session user's display choices, as the back-office keeps them for them. */
export interface UserPreferences {
  /** «Métricas globales»: every figure over the whole dataset, on every screen. */
  globalMetrics: boolean;
  /** The preferred score method: the one every screen shows its figures under. */
  scoreMethod: ScoreMethod;
  /** The players' speed, on every video. */
  playbackRate: PlaybackRate;
}

interface PreferencesPayload {
  globalMetrics?: boolean;
  scoreMethod?: string;
  playbackRate?: number;
}

function toPreferences(payload: PreferencesPayload | null | undefined): UserPreferences {
  const method = payload?.scoreMethod as ScoreMethod | undefined;

  return {
    globalMetrics: payload?.globalMetrics === true,
    // An API older than the choice, or a method this build does not know: the main one.
    scoreMethod: method && METHOD_ORDER.includes(method) ? method : DEFAULT_SCORE_METHOD,
    playbackRate: isPlaybackRate(payload?.playbackRate)
      ? payload.playbackRate
      : DEFAULT_PLAYBACK_RATE,
  };
}

/**
 * Boundary with the session's preferences (`/session/preferences`).
 *
 * Kept per user on the server (product owner, 2026-09-27): the choice follows the person from one
 * browser to the next. The answer always carries every preference, defaults included.
 */
export const preferencesApi = {
  get: async (): Promise<UserPreferences> =>
    toPreferences(await httpClient.get<PreferencesPayload>('/session/preferences')),

  update: async (change: Partial<UserPreferences>): Promise<UserPreferences> =>
    toPreferences(await httpClient.put<PreferencesPayload>('/session/preferences', change)),
};
